import { useEffect, useState, useCallback, useRef } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { Message } from '@/types/database'

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseRealtimeMessagesReturn {
  messages: Message[]
  sendMessage: (content: string) => Promise<void>
  isLoading: boolean
}

/**
 * Subscribes to the real-time messages channel for a given `bookingId`.
 *
 * On mount:
 *  1. Fetches all existing messages for the booking from the DB.
 *  2. Subscribes to the Supabase Realtime channel for that booking so new
 *     messages appear instantly.
 *
 * On unmount:
 *  - Unsubscribes from the channel and cleans up.
 *
 * @param bookingId - The UUID of the booking whose messages to subscribe to.
 */
function useRealtimeMessages(bookingId: string): UseRealtimeMessagesReturn {
  const { user } = useAuth()
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const channelRef = useRef<RealtimeChannel | null>(null)

  // ── Initial fetch ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!bookingId) return

    let mounted = true

    async function loadMessages() {
      setIsLoading(true)
      try {
        const { data, error } = await supabase
          .from('messages')
          .select('*')
          .eq('booking_id', bookingId)
          .order('created_at', { ascending: true })

        if (!mounted) return
        if (error) {
          Sentry.captureException(error, { tags: { source: 'useRealtimeMessages.fetch' } })
          if (import.meta.env.DEV) {
            console.error('[useRealtimeMessages] fetch error:', error.message)
          }
          return
        }

        setMessages((data ?? []) as Message[])
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    loadMessages()

    return () => {
      mounted = false
    }
  }, [bookingId])

  // ── Real-time subscription ─────────────────────────────────────────────────

  useEffect(() => {
    if (!bookingId) return

    const channelName = `messages:booking_id=eq.${bookingId}`

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `booking_id=eq.${bookingId}`,
        },
        (payload) => {
          const newMessage = payload.new as Message
          setMessages((prev) => {
            // Guard against duplicate messages (edge case with optimistic updates)
            const alreadyExists = prev.some((m) => m.id === newMessage.id)
            return alreadyExists ? prev : [...prev, newMessage]
          })
        },
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          Sentry.captureMessage(`useRealtimeMessages channel error on ${channelName}`, 'error')
          if (import.meta.env.DEV) {
            console.error(
              `[useRealtimeMessages] channel error on ${channelName}`,
            )
          }
        }
      })

    channelRef.current = channel

    return () => {
      supabase.removeChannel(channel)
      channelRef.current = null
    }
  }, [bookingId])

  // ── sendMessage ────────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    async (content: string) => {
      if (!user?.id) throw new Error('Unauthenticated')
      if (!content.trim()) return

      const { error } = await supabase.from('messages').insert({
        booking_id: bookingId,
        sender_id: user.id,
        content: content.trim(),
      })

      if (error) {
        throw new Error(error.message)
      }
    },
    [bookingId, user?.id],
  )

  return { messages, sendMessage, isLoading }
}

export { useRealtimeMessages }
